import { useState, useSyncExternalStore } from "react";
import { ManualOrderImport } from "./ManualOrderImport";
import { parseImportSelection, selectionLabel } from "../../../../packages/shared/order-import/selection";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  InlineStack,
  Modal,
  Select,
  Text,
} from "@shopify/polaris";
import {
  createImportCommands,
  type ImportAction,
} from "../../../../packages/shared/order-import/client";
import {
  queueAccountKey,
  type QueueAccount,
} from "../../../../packages/shared/shopify-queue/client";
type ImportRun = {
  id: string;
  connection_id: string;
  domain: string;
  source: string;
  state: string;
  items_imported: number;
  pages_imported: number;
  revision: number;
  attempts: number;
  error: string | null;
  window_start: string;
  window_end: string;
  created_count: number;
  updated_count: number;
  skipped_count: number;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
  selection: string | null;
  reading_lines: number;
  reading_pages: number;
};
export type ImportData = {
  stores: { id: string; domain: string; connected: number }[];
  selected_connection_id: string | null;
  connection: { id: string; domain: string; connected: number } | null;
  schedule: {
    connection_id: string;
    domain: string;
    enabled: number;
    revision: number;
    interval_minutes: number;
    next_run_at: string | null;
  } | null;
  runs: ImportRun[];
  total: number;
  nextCursor: number | null;
  legacySchedules: unknown[];
};
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com";
const commands = new Map<string, ReturnType<typeof createImportCommands>>();
const storage = {
  get length() {
    return window.localStorage.length;
  },
  key: (i: number) => window.localStorage.key(i),
  getItem: (k: string) => window.localStorage.getItem(k),
  setItem: (k: string, v: string) => window.localStorage.setItem(k, v),
  removeItem: (k: string) => window.localStorage.removeItem(k),
};
export function ImportControls({
  data,
  account,
  canImport,
  canSchedule,
  onSaved,
}: {
  data: ImportData;
  account: QueueAccount;
  canImport: boolean;
  canSchedule: boolean;
  onSaved: () => void;
}) {
  const key = JSON.stringify([base, queueAccountKey(account), data.selected_connection_id]);
  if (!commands.has(key))
    commands.set(key, createImportCommands(base, account, storage, data.selected_connection_id || undefined));
  const command = commands.get(key)!;
  const state = useSyncExternalStore(command.subscribe, command.snapshot),
    [review, setReview] = useState<ImportAction | null>(null),
    [configure, setConfigure] = useState(false),
    [enabled, setEnabled] = useState(!!data.schedule?.enabled),
    [interval, setInterval] = useState(
      String(data.schedule?.interval_minutes || 60),
    );
  const actionLocked = state.busy || !!state.pending || !canImport,
    locked = actionLocked || !data.connection?.connected;
  const action = (
    value: Partial<ImportAction> & { action: ImportAction["action"] },
  ) => {
    if (!data.connection && !data.schedule && !value.connection_id) return;
    setReview({
      connection_id: data.connection?.id || data.schedule?.connection_id || "",
      domain: data.connection?.domain || data.schedule?.domain || "",
      revision: 0,
      ...value,
    });
  };
  const send = async () => {
    if (!review) return;
    const value = review;
    setReview(null);
    await command.submit(value);
    if (command.snapshot().success) onSaved();
  };
  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">
            Order imports
          </Text>
          <Text as="p">
            Import orders from{" "}
            {data.connection?.domain || "your connected Shopify store"}. Work
            continues after you close this page. Physical stock, reservations,
            costs and completed warehouse work stay unchanged.
          </Text>
          <Text as="p">
            Choose dates and statuses for a manual import. Automatic imports
            cover the previous 59 days with any payment or fulfillment status. New
            orders await warehouse allocation. Unpaid, cancelled or already
            fulfilled orders are held for review. Conflicting warehouse work
            pauses the import. Older order history needs a separate import with
            the right Shopify access.
          </Text>
          {!data.connection?.connected && (
            <Banner tone="warning">
              Connect a Shopify store before importing orders.
            </Banner>
          )}
          {state.error && <Banner tone="critical">{state.error}</Banner>}
          {state.success && <Banner tone="success">{state.success}</Banner>}
          {state.pending && (
            <Banner tone="warning">
              <BlockStack gap="200">
                <Text as="p">
                  A saved import action for {JSON.parse(state.pending.body).domain} needs a confirmed reply. It remains
                  saved after you close the browser.
                </Text>
                <Button
                  loading={state.busy}
                  onClick={() =>
                    void command.retry().then(() => {
                      if (command.snapshot().success) onSaved();
                    })
                  }
                >
                  Retry saved action
                </Button>
              </BlockStack>
            </Banner>
          )}
          {data.connection && <ManualOrderImport key={key + data.connection.id + data.connection.domain} account={account} connection={{id:data.connection.id,domain:data.connection.domain}} locked={locked} onConfirm={async (selection_review, draft) => {
            await command.submit({action:"start",connection_id:data.connection!.id,domain:data.connection!.domain,revision:0,selection_review},draft);
            if(command.snapshot().success) onSaved();
          }} />}
          <Text as="p">
            {data.schedule?.enabled
              ? `Automatic import every ${data.schedule.interval_minutes} minutes. Next check: ${data.schedule.next_run_at || "pending"} UTC.`
              : "Automatic order imports are off."}
          </Text>
          <Text as="p">
            An active import finishes its pages before another starts for this store. Resolve a
            failed import before starting another for the same store.
          </Text>
          <Button
            disabled={
              actionLocked ||
              !canSchedule ||
              (!data.connection && !data.schedule)
            }
            onClick={() => {
              setEnabled(!!data.schedule?.enabled);
              setInterval(String(data.schedule?.interval_minutes || 60));
              setConfigure(true);
            }}
          >
            Configure order schedule
          </Button>
        </BlockStack>
      </Card>
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">
            Import history
          </Text>
          <Text as="p">
            {data.total} saved imports. Counts cover orders processed within
            each saved date window.
          </Text>
          {data.runs.map((run) => (
            <BlockStack key={run.id} gap="200">
              <Text as="h3" variant="headingSm">
                {run.state} ·{" "}
                {run.source === "schedule" ? "Scheduled" : "Staff requested"} ·{" "}
                {run.created_at} UTC
              </Text>
              <Text as="p">
                {run.items_imported} orders processed in {run.pages_imported}{" "}
                saved batches. Last update: {run.updated_at} UTC.
              </Text>
              <Text as="p">
                Window: {run.window_start} to {run.window_end}. Created:{" "}
                {run.created_count}. Updated: {run.updated_count}. Skipped stale
                or removed records: {run.skipped_count}.
              </Text>
              {run.reading_lines > 0 && <Text as="p">{run.reading_lines} lines kept in {run.reading_pages} temporary pages. They have not been applied to warehouse orders. Reads continue from saved progress; temporary pages expire after seven days and are then read again.</Text>}
              {run.error && <Banner tone="warning">{run.error}</Banner>}
              <Text as="p">{run.selection ? (() => { try { return selectionLabel(parseImportSelection(JSON.parse(run.selection))); } catch { return "Saved filters could not be read. Review this import before retrying."; } })() : "Payment: any. Fulfillment: any."}</Text>
              <InlineStack gap="200">
                {run.state === "failed" && (
                  <Button
                    disabled={locked}
                    onClick={() =>
                      action({
                        action: "retry",
                        run_id: run.id,
                        revision: run.revision,
                        connection_id: run.connection_id,
                        domain: run.domain,
                      })
                    }
                  >
                    Review retry
                  </Button>
                )}
                {["pending", "running", "failed"].includes(run.state) && (
                  <Button
                    disabled={actionLocked}
                    onClick={() =>
                      action({
                        action: "cancel",
                        run_id: run.id,
                        revision: run.revision,
                        connection_id: run.connection_id,
                        domain: run.domain,
                      })
                    }
                  >
                    Review cancellation
                  </Button>
                )}
              </InlineStack>
            </BlockStack>
          ))}
          {!data.runs.length && (
            <Text as="p">No order imports have been requested.</Text>
          )}
        </BlockStack>
      </Card>
      <Modal
        open={configure}
        title="Order import schedule"
        onClose={() => setConfigure(false)}
        primaryAction={{
          content: "Review schedule",
          onAction: () => {
            setConfigure(false);
            action({
              action: "schedule",
              revision: data.schedule?.revision || 0,
              enabled,
              interval_minutes: Number(interval),
            });
          },
        }}
        secondaryActions={[
          { content: "Cancel", onAction: () => setConfigure(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Checkbox
              label="Enable automatic order imports"
              checked={enabled}
              onChange={setEnabled}
            />
            <Select
              label="Import interval"
              value={interval}
              options={[15, 30, 60, 120, 240, 360, 720, 1440].map((v) => ({
                label: v < 60 ? `Every ${v} minutes` : `Every ${v / 60} hours`,
                value: String(v),
              }))}
              onChange={setInterval}
            />
            <Text as="p">
              This schedule uses your current order import access. Removing that
              access stops future work. Changing or disabling the schedule stops
              later pages of its active import.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Modal
        open={!!review}
        title="Review order import action"
        onClose={() => setReview(null)}
        primaryAction={{
          content: "Confirm import action",
          disabled:
            review?.action === "cancel" ||
            (review?.action === "schedule" && !review.enabled)
              ? actionLocked
              : locked,
          onAction: () => void send(),
        }}
        secondaryActions={[
          { content: "Cancel", onAction: () => setReview(null) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p">Store: {review?.domain}</Text>
            <Text as="p">
              {review?.action === "schedule"
                  ? `${review.enabled ? "Enable" : "Disable"} automatic order imports${review.enabled ? ` every ${review.interval_minutes} minutes` : ""}.`
                  : review?.action === "retry"
                    ? "Continue the selected import from its last saved order page."
                    : "Cancel the remaining work for this import. Completed order pages remain saved."}
            </Text>
            <Text as="p">
              Shopify sends orders in pages. The date window is fixed when the
              import is queued. Completed means all accessible orders in that
              window were processed. It does not confirm a pick, shipment or
              delivery.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </BlockStack>
  );
}
